import { useCallback, useEffect, useRef, useState } from 'react';
import { LAYOUT_STORAGE_VERSION } from '../../domain';
import type {
    PersistedLayout,
    PersistedQueryPatch,
    TableSettings,
} from '../../domain';
import type { StorageAdapter } from '../../ports';
import { DEBOUNCE, STORAGE_PREFIX } from '../../defaults';

export interface UseSettingsArgs {
    id: string;
    storage?: StorageAdapter;
}

const EMPTY_LAYOUT: PersistedLayout = { v: LAYOUT_STORAGE_VERSION };

function storageKey(id: string): string {
    return `${STORAGE_PREFIX}${id}`;
}

export function readLayout(
    storage: StorageAdapter,
    id: string,
): PersistedLayout {
    const raw = storage.getItem(storageKey(id));
    if (!raw) return EMPTY_LAYOUT;
    try {
        const parsed = JSON.parse(raw) as Partial<PersistedLayout> | null;
        if (!parsed || parsed.v !== LAYOUT_STORAGE_VERSION) return EMPTY_LAYOUT;
        return parsed as PersistedLayout;
    } catch {
        return EMPTY_LAYOUT;
    }
}

function writeLayout(
    storage: StorageAdapter,
    id: string,
    layout: PersistedLayout,
): void {
    try {
        storage.setItem(storageKey(id), JSON.stringify(layout));
    } catch (_err) {
        void _err;
    }
}

export function useSettings(args: UseSettingsArgs): TableSettings {
    const { id, storage = localStorage } = args;
    const [layout, setLayout] = useState<PersistedLayout>(() =>
        readLayout(storage, id),
    );
    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const clearPending = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = undefined;
    }, []);

    const flushPending = useCallback(() => {
        if (!timer.current) return;
        clearPending();
        writeLayout(storage, id, layoutRef.current);
    }, [clearPending, id, storage]);

    const commit = useCallback(
        (next: PersistedLayout) => {
            layoutRef.current = next;
            setLayout(next);
            clearPending();
            timer.current = setTimeout(() => {
                timer.current = undefined;
                writeLayout(storage, id, next);
            }, DEBOUNCE.layout);
        },
        [clearPending, id, storage],
    );

    const setWidth = useCallback(
        (columnId: string, width: number) => {
            commit({
                ...layoutRef.current,
                columnWidths: {
                    ...layoutRef.current.columnWidths,
                    [columnId]: width,
                },
            });
        },
        [commit],
    );

    const setOrder = useCallback(
        (columnIds: string[]) => {
            commit({ ...layoutRef.current, columnOrder: columnIds });
        },
        [commit],
    );

    const setHidden = useCallback(
        (columnId: string, hidden: boolean) => {
            commit({
                ...layoutRef.current,
                columnVisibility: {
                    ...layoutRef.current.columnVisibility,
                    [columnId]: !hidden,
                },
            });
        },
        [commit],
    );

    const setGroupCollapsed = useCallback(
        (groupKey: string, collapsed: boolean) => {
            commit({
                ...layoutRef.current,
                groupCollapsed: {
                    ...layoutRef.current.groupCollapsed,
                    [groupKey]: collapsed,
                },
            });
        },
        [commit],
    );

    const setQuery = useCallback(
        (patch: PersistedQueryPatch) => {
            commit({ ...layoutRef.current, ...patch });
        },
        [commit],
    );

    const reset = useCallback(() => {
        clearPending();
        try {
            storage.removeItem(storageKey(id));
        } catch (_err) {
            void _err;
        }
        setLayout(EMPTY_LAYOUT);
    }, [clearPending, id, storage]);

    useEffect(() => {
        window.addEventListener('pagehide', flushPending);
        return () => {
            window.removeEventListener('pagehide', flushPending);
            flushPending();
        };
    }, [flushPending]);

    return {
        layout,
        setWidth,
        setOrder,
        setHidden,
        setGroupCollapsed,
        setQuery,
        reset,
    };
}
